import * as THREE from 'three';
import { createRng, seedFromUrl } from './runtime/rng.js';
import { buildPropertyArchetype } from './builders/archetypes.js';
import { limewashTexture } from './builders/textures.js';
import { cel } from './core/toon.js';
import { addWorldExtras } from './scene-extras.js';
import { resolveEntityTokens } from './tokens/resolver.js';

// Spatial choreography adapted from our own Rubik Sota Altea Foundation-08:
// entry -> narrow lane -> threshold turn -> passage -> plaza -> mirador -> Mediterranean reveal.
// This file intentionally re-authors the visual language instead of copying the source toon identity.
//
// 2026-08-17 (Kimi K3): seeded RNG (deterministic QA), collider + walkable
// registration for the first-person walker (sakura-derived collision model).

const C = Object.freeze({
  ivory: 0xf1eee5,
  plaster: 0xf7f4ec,
  plasterWarm: 0xe8dfd0,
  stone: 0xc9bca8,
  cobble: 0xbab3a8,
  ink: 0x20352f,
  blue: 0x326f91,
  blueDark: 0x214e68,
  terracotta: 0xa6654b,
  sea: 0x6eb9c7,
  seaDeep: 0x397f96,
  green: 0x5e7b63,
  greenDark: 0x365844,
  flower: 0xb35f72,
  sand: 0xd7c7a8,
  road: 0x777872
});

// Cel-shaded world materials (Sorolla colored shadows — src/core/toon.js).
// roughness/metalness args are kept for call-site compatibility but ignored:
// cel shading quantises light into bands instead of PBR falloff.
function mat(color, _roughness = .9, _metalness = 0, { wash = false, bands = 3 } = {}) {
  return cel({ color, bands, map: wash ? limewashTexture() : null });
}

const M = {
  plaster: mat(C.plaster, .95, 0, { wash: true, bands: 'soft3' }),
  plasterWarm: mat(C.plasterWarm, .98, 0, { wash: true, bands: 'soft3' }),
  stone: mat(C.stone, 1, 0, { wash: true }),
  cobble: mat(C.cobble, 1, 0, { wash: true }),
  ink: mat(C.ink, .8),
  blue: mat(C.blue, .75),
  blueDark: mat(C.blueDark, .72),
  terracotta: mat(C.terracotta, .95),
  seaDeep: mat(C.seaDeep, .55),
  green: mat(C.green, 1),
  greenDark: mat(C.greenDark, 1),
  pine: mat(0x4f6b52, 1),
  bougainvillea: [mat(0xc74d7c, .95, 0, { bands: 'soft3' }), mat(0xd4679a, .95, 0, { bands: 'soft3' }), mat(0xb83e6e, .95, 0, { bands: 'soft3' })],
  flower: mat(C.flower, .9, 0, { bands: 'soft3' }),
  sand: mat(C.sand, 1, 0, { wash: true, bands: 'soft3' }),
  road: mat(C.road, 1),
  glass: new THREE.MeshPhysicalMaterial({ color: 0x9fc6cf, roughness: .15, metalness: 0, transparent: true, opacity: .55 }),
  white: mat(0xffffff, .82, 0, { bands: 'soft3' })
};

const STEP_UP = 0.38; // matches WALKER.STEP

export function buildAlteaSlice(scene, semanticWorld, { seed } = {}) {
  const rng = createRng(seed ?? seedFromUrl(new URLSearchParams(location.search)));
  const root = new THREE.Group();
  root.name = 'altea-slice-v0';
  scene.add(root);

  const interactables = [];
  const anchors = new Map();
  const colliders = [];
  const walkables = [];

  const ctx = { rng, colliders, walkables };

  addTerrain(root, ctx);
  addSea(root);
  addPromenade(root, ctx);
  addOldTownSequence(root, interactables, semanticWorld, anchors, ctx);
  addHillside(root, interactables, semanticWorld, anchors, ctx);
  addSeafrontProperties(root, interactables, semanticWorld, anchors, ctx);
  addMarina(root);
  addDistantContext(root);
  addWorldExtras(root, ctx); // identity silhouette, props, expansion, life

  const collision = buildCollisionWorld(colliders, walkables);
  return { root, interactables, anchors, collision };
}

function buildCollisionWorld(colliders, walkables) {
  return {
    colliders,
    bounds: { x0: -20, x1: 20, z0: -13.5, z1: 13 },
    heightAt(x, z, feetY = Infinity) {
      let best = -0.02; // land slab top
      for (const w of walkables) {
        if (x < w.x0 || x > w.x1 || z < w.z0 || z > w.z1) continue;
        if (w.top > feetY + STEP_UP) continue;
        if (w.top > best) best = w.top;
      }
      return best;
    }
  };
}

// Rotate a local (x,z) into world space for a group yaw, then return an
// axis-aligned box with a small margin for the rotation error.
function rotatedBox(cx, cz, w, d, yaw, px, pz) {
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  const wx = px + cx * cos + cz * sin;
  const wz = pz - cx * sin + cz * cos;
  const m = Math.abs(sin) * (w + d) * 0.5;
  return { x0: wx - w / 2 - m, x1: wx + w / 2 + m, z0: wz - d / 2 - m, z1: wz + d / 2 + m };
}

function addCollider(ctx, box, bottom, top) {
  ctx.colliders.push({ ...box, bottom, top });
}

function addWalkable(ctx, box, top) {
  ctx.walkables.push({ ...box, top });
}

function addTerrain(root, ctx) {
  const land = meshBox(44, .8, 33, M.sand, 0, -.42, 0);
  land.rotation.y = -.055;
  root.add(land);

  const hill = new THREE.Mesh(new THREE.CylinderGeometry(11.5, 18, 7, 8, 1), M.stone);
  hill.position.set(8, 1.0, -4.5);
  hill.rotation.y = .17;
  root.add(hill);
  // hill top is a reachable terrace in this blockout
  addWalkable(ctx, { x0: 8 - 8.5, x1: 8 + 8.5, z0: -4.5 - 7.5, z1: -4.5 + 7.5 }, 4.5);

  const greenPatch = new THREE.Mesh(new THREE.CylinderGeometry(7, 8.2, .35, 8), M.green);
  greenPatch.position.set(12.4, 4.4, -7.4);
  root.add(greenPatch);
}

function addSea(root) {
  // Mediterranean water: shore turquoise -> offshore deep blue vertex
  // gradient + additive sun glitter + foam line (mission: water must carry
  // light and sky, not read as a flat blue plane).
  const geo = new THREE.PlaneGeometry(75, 54, 48, 36); // denser = smooth gradient
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const near = new THREE.Color(0x8fd0c8);
  const mid = new THREE.Color(0x4b96ad);
  const deep = new THREE.Color(0x2f6d8c);
  const c = new THREE.Color();
  for (let i = 0; i < pos.count; i += 1) {
    const t = THREE.MathUtils.clamp((pos.getZ(i) + 27) / 54, 0, 1); // 0 near shore
    if (t < .32) c.lerpColors(near, mid, t / .32);
    else c.lerpColors(mid, deep, (t - .32) / .68);
    colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const sea = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: .17, metalness: .12 }));
  sea.position.set(-10, -.055, 31); // below land top (-.02): no z-fight strip
  sea.receiveShadow = true;
  root.add(sea);

  const glitter = new THREE.Mesh(
    new THREE.PlaneGeometry(30, 9),
    new THREE.MeshBasicMaterial({ color: 0xfff0c8, transparent: true, opacity: .15, blending: THREE.AdditiveBlending, depthWrite: false })
  );
  glitter.rotation.x = -Math.PI / 2;
  glitter.rotation.z = -.32;
  glitter.position.set(-15, .035, 24);
  root.add(glitter);

  const foam = new THREE.Mesh(
    new THREE.PlaneGeometry(46, .7),
    new THREE.MeshBasicMaterial({ color: 0xf6fbf7, transparent: true, opacity: .5, depthWrite: false })
  );
  foam.rotation.x = -Math.PI / 2;
  foam.rotation.z = -.04;
  foam.position.set(-1, .04, 15.9);
  root.add(foam);

  const horizon = new THREE.Mesh(new THREE.PlaneGeometry(100, 16), new THREE.MeshBasicMaterial({ color: 0xd8e4de, transparent: true, opacity: .3, depthWrite: false }));
  horizon.position.set(-4, 8, 50);
  root.add(horizon);

  const shore = new THREE.Mesh(new THREE.PlaneGeometry(48, 5), M.sand);
  shore.rotation.x = -Math.PI / 2;
  shore.rotation.z = -.04;
  shore.position.set(-1, .025, 13.4);
  root.add(shore);
}

function addPromenade(root, ctx) {
  const promenade = meshBox(35, .18, 2.2, M.plasterWarm, -2, .12, 9.8);
  promenade.rotation.y = -.035;
  root.add(promenade);
  addWalkable(ctx, rotatedBox(-2, 9.8, 35, 2.2, -.035, 0, 0), .21);

  for (let x = -14; x <= 12; x += 5.2) {
    addPalm(root, x, .1, 8.8, .85 + ctx.rng() * .18, ctx);
  }

  const road = meshBox(34, .08, 2.3, M.road, -1, .08, 6.8);
  road.rotation.y = -.035;
  root.add(road);
  addWalkable(ctx, rotatedBox(-1, 6.8, 34, 2.3, -.035, 0, 0), .12);

  for (let i = 0; i < 5; i++) {
    const cx = -10 + i * 5.3, cz = 6.75 + (i % 2) * .15;
    const car = meshBox(1.15, .42, .55, i % 2 ? M.blueDark : M.terracotta, cx, .34, cz);
    car.rotation.y = -.035;
    root.add(car);
    addCollider(ctx, rotatedBox(cx, cz, 1.15, .55, -.035, 0, 0), .1, .6);
  }

  // promenade pots + one umbrella pine at the east end
  for (const [px, pz] of [[-11.5, 9.4], [2, 9.35], [9, 9.45]]) {
    addFlowerPot(root, px, .21, pz);
    addCollider(ctx, { x0: px - .16, x1: px + .16, z0: pz - .16, z1: pz + .16 }, .21, .66);
  }
  addPine(root, 13.6, .1, 8.6, 1.1);
}

function addOldTownSequence(root, interactables, semanticWorld, anchors, ctx) {
  const seq = new THREE.Group();
  seq.position.set(-7, .3, -1);
  seq.rotation.y = -.08;
  root.add(seq);
  const SY = -.08, SP = [-7, -1]; // yaw + world (x,z) of the group origin

  const levels = [0, .65, 1.2, 1.85, 2.55, 3.15];
  const points = [
    [-1, 3.5], [1.7, 2.6], [3.8, .7], [6.1, -1.0], [8.7, -2.0], [12.2, -2.4]
  ];

  for (let i = 0; i < points.length; i++) {
    const [x, z] = points[i];
    const w = i < 4 ? 4.0 : 5.2, d = i === 2 ? 3.3 : 3.8;
    const platform = meshBox(w, .25, d, i === 4 || i === 5 ? M.plasterWarm : M.cobble, x, levels[i] - .08, z);
    platform.receiveShadow = true;
    seq.add(platform);
    addWalkable(ctx, rotatedBox(x, z, w, d, SY, SP[0], SP[1]), .3 + levels[i] + .045);
    if (i < points.length - 1) {
      const next = points[i + 1];
      addSteps(seq, x + (next[0] - x) * .55, z + (next[1] - z) * .55, levels[i], levels[i + 1], 4, Math.atan2(next[0] - x, next[1] - z), ctx, SY, SP);
    }
  }

  addPortal(seq, 3.45, 1.18, 1.1, -.45, ctx, SY, SP);

  const houses = [
    [-2.2, 4.7, 0, 2.7, 2.2],
    [.3, 5.0, 0, 2.4, 2.0],
    // [1.6,.25,.65] removed — it floated (no platform beneath); its plot is
    // now property.oldtown-05, a real oldtown-house archetype on platform 1.
    [4.9, 2.9, 1.2, 2.4, 2.1],
    [5.5, -3.25, 1.85, 2.3, 2.0],
    // [9.3,.9,2.55] removed — it stood directly on the SE approach sightline
    // to property.family-02 and filled the detail-card frame (QA critique).
    [10.7, -4.5, 3.15, 2.6, 2.2]
  ];
  const TRIMS = [M.blue, M.terracotta, M.greenDark];
  houses.forEach((h, i) => {
    const [x, z, y, w, d] = h;
    const floors = i % 4 === 0 ? 3 : 2;
    addMediterraneanHouse(seq, x, z, y, w, d, { accent: i % 3 === 0, floors, trim: TRIMS[i % 3] });
    addCollider(ctx, rotatedBox(x, z, w, d, SY, SP[0], SP[1]), .3 + y, .3 + y + floors * 1.55);
  });

  addPlazaDetails(seq, 7.2, 2.55, -2.4, ctx, SY, SP);
  addMirador(seq, 12.2, 3.15, -2.4, ctx, SY, SP);

  const propertyB = semanticWorld.entities.find((e) => e.id === 'property.family-02');
  const propertyE = semanticWorld.entities.find((e) => e.id === 'property.oldtown-05');
  const sarah = semanticWorld.entities.find((e) => e.id === 'entity.sarah.world-guide');
  const neighbour = semanticWorld.entities.find((e) => e.id === 'character.neighbour.anna-tom');

  // family-villa on platform 4 east (clear SE sightline for the approach
  // camera), oldtown-house on platform 1.
  if (propertyB) addPropertyBuilding(seq, propertyB, semanticWorld, ctx.rng, 9.4, 2.595, -2.3, interactables, anchors, ctx, { yaw: SY, origin: SP, baseY: .3 + 2.595 });
  if (propertyE) addPropertyBuilding(seq, propertyE, semanticWorld, ctx.rng, 1.4, .695, 2.2, interactables, anchors, ctx, { yaw: SY, origin: SP, baseY: .3 + .695 });
  if (sarah) addCharacterMarker(seq, sarah, -.2, .35, 2.55, interactables, anchors, M.ink, SY, SP);
  if (neighbour) addCharacterMarker(seq, neighbour, 7.6, 2.85, -.9, interactables, anchors, M.greenDark, SY, SP);

  // Altea micro-DNA: bougainvillea cascades on lane facades (seq-local)
  addBougainvillea(seq, .8, 1.5, 3.3, 1, ctx.rng);    // beside oldtown-05 door
  addBougainvillea(seq, -2.9, 1.5, 3.65, 1.1, ctx.rng); // lane-entry house
  addBougainvillea(seq, 2.62, 2.3, 1.4, .9, ctx.rng);  // portal pillar
  addBougainvillea(seq, 8.7, 3.7, -1.05, 1, ctx.rng);  // family-villa facade
}

function addHillside(root, interactables, semanticWorld, anchors, ctx) {
  const propertyC = semanticWorld.entities.find((e) => e.id === 'property.hills-03');
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const x = 12.3 + Math.cos(a) * (5.2 + (i % 2));
    const z = -7.5 + Math.sin(a) * 4.2;
    const y = 4.55 + (i % 3) * .15;
    addMediterraneanHouse(root, x, z, y, 2.4, 2.25, { floors: 2, accent: i === 2 });
    addCollider(ctx, { x0: x - 1.2, x1: x + 1.2, z0: z - 1.125, z1: z + 1.125 }, y, y + 3.1);
  }
  if (propertyC) addPropertyBuilding(root, propertyC, semanticWorld, ctx.rng, 13.8, 4.58, -7.6, interactables, anchors, ctx, { baseY: 4.58 });

  for (let i = 0; i < 10; i++) addTree(root, 6 + (i % 5) * 3.2, 4.6, -11 + Math.floor(i / 5) * 3.4, .7 + (i % 3) * .12, ctx);
  addPine(root, 3, 4.5, -9.6, 1.2);
  addPine(root, 12.2, 4.5, -11.4, 1.0);
}

// Seafront plots: sea-01 (modern sea-villa) east of the promenade and
// marina-04 (townhouse) at the west end near the pier. Both walk-up-able.
function addSeafrontProperties(root, interactables, semanticWorld, anchors, ctx) {
  const propertyA = semanticWorld.entities.find((e) => e.id === 'property.sea-01');
  const propertyD = semanticWorld.entities.find((e) => e.id === 'property.marina-04');
  if (propertyA) addPropertyBuilding(root, propertyA, semanticWorld, ctx.rng, 7, .03, 11.6, interactables, anchors, ctx, { baseY: .03 });
  if (propertyD) addPropertyBuilding(root, propertyD, semanticWorld, ctx.rng, -12.5, .03, 11.7, interactables, anchors, ctx, { baseY: .03 });
  addBougainvillea(root, -13.5, .8, 13.85, 1, ctx.rng); // marina-04 patio wall
}

function addMarina(root) {
  const pier = meshBox(13, .18, 1.2, M.plasterWarm, -14, .12, 17.6);
  pier.rotation.y = .08;
  root.add(pier);
  for (let i = 0; i < 5; i++) {
    const boat = new THREE.Group();
    boat.add(meshBox(1.45, .18, .5, i % 2 ? M.white : M.blue, 0, .12, 0));
    const mast = meshBox(.045, 2.2, .045, M.ink, .1, 1.1, 0);
    boat.add(mast);
    const sailGeo = new THREE.BufferGeometry();
    sailGeo.setAttribute('position', new THREE.Float32BufferAttribute([.12,.15,0, .12,2.0,0, 1.0,.28,0], 3));
    sailGeo.setIndex([0,1,2]);
    sailGeo.computeVertexNormals();
    boat.add(new THREE.Mesh(sailGeo, M.white));
    boat.position.set(-18 + i * 2.8, .2, 21 + (i % 2) * 1.2);
    boat.rotation.y = -.12 + i * .03;
    root.add(boat);
  }
}

function addDistantContext(root) {
  const town = new THREE.Group();
  // ground slab — the row extends past the main landmass edge (x < -22),
  // without this the far boxes float over the void in overview shots.
  town.add(meshBox(29, .35, 6.5, M.sand, 12.75, -.18, 1.2));
  for (let i = 0; i < 18; i++) {
    const w = 1.2 + (i % 3) * .35;
    const h = .8 + (i % 4) * .35;
    town.add(meshBox(w, h, 1.1, i % 5 === 0 ? M.plasterWarm : M.plaster, i * 1.5, h / 2, (i % 3) * 1.1));
  }
  town.position.set(-27, 0, 0);
  town.rotation.y = .08;
  root.add(town);

  const mountainMat = cel({ color: 0x78918a, bands: 3 });
  for (let i = 0; i < 5; i++) {
    const m = new THREE.Mesh(new THREE.ConeGeometry(7 + i * 1.4, 7 + (i % 2) * 3, 5), mountainMat);
    m.position.set(-31 + i * 9.5, 3.2, -20 - (i % 2) * 2);
    m.rotation.y = i * .27;
    root.add(m);
  }
}

function addMediterraneanHouse(root, x, z, y, w, d, { floors = 2, accent = false, trim = M.blue } = {}) {
  const g = new THREE.Group();
  const height = floors * 1.55;
  const body = meshBox(w, height, d, accent ? M.plasterWarm : M.plaster, 0, height / 2, 0);
  body.castShadow = body.receiveShadow = true;
  g.add(body);

  const roof = meshBox(w + .12, .16, d + .12, accent ? M.terracotta : M.plasterWarm, 0, height + .08, 0);
  g.add(roof);

  for (let floor = 0; floor < floors; floor++) {
    const wy = .65 + floor * 1.48;
    for (const wx of [-w * .25, w * .25]) {
      const window = meshBox(.34, .55, .035, trim, wx, wy, d / 2 + .025);
      g.add(window);
    }
    if (floor === 0) {
      const door = meshBox(.46, .82, .04, trim, 0, .43, d / 2 + .028);
      g.add(door);
    }
  }

  // small side windows so gable walls never read as blank slabs (QA critique:
  // sarah-encounter framed a featureless side facade)
  for (let floor = 0; floor < floors; floor += 1) {
    const wy = .75 + floor * 1.48;
    for (const sx of [-1, 1]) {
      const side = meshBox(.035, .42, .3, trim, sx * (w / 2 + .025), wy, d * .12);
      g.add(side);
    }
  }

  const balcony = meshBox(w * .55, .06, .48, M.ink, 0, 1.55, d / 2 + .28);
  if (floors > 1) g.add(balcony);

  g.position.set(x, y, z);
  root.add(g);
  return g;
}

// Place a full property archetype (docs/32 recipes A-E) instead of a bare
// beacon marker. Registers collider + world-space anchor like the markers do.
function addPropertyBuilding(root, entity, semanticWorld, rng, x, y, z, interactables, anchors, ctx, { yaw = 0, origin = [0, 0], baseY = y } = {}) {
  const tokens = resolveEntityTokens(semanticWorld, entity);
  const { group, footprint, height } = buildPropertyArchetype(tokens, rng);
  group.position.set(x, y, z);
  group.userData.entityId = entity.id;
  group.userData.entityKind = entity.kind;
  group.userData.tokens = tokens;
  root.add(group);
  interactables.push(group);
  addCollider(ctx, rotatedBox(x, z, footprint.w, footprint.d, yaw, origin[0], origin[1]), baseY, baseY + height);
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  anchors.set(entity.id, new THREE.Vector3(origin[0] + x * cos + z * sin, baseY, origin[1] - x * sin + z * cos));
}

function addPropertyMarker(root, entity, x, y, z, interactables, anchors, material, yaw = 0, origin = [0, 0]) {
  const group = new THREE.Group();
  group.position.set(x, y, z);
  group.userData.entityId = entity.id;
  group.userData.entityKind = entity.kind;

  const base = new THREE.Mesh(new THREE.CylinderGeometry(.42, .52, .12, 24), material);
  base.position.y = .06;
  const beacon = new THREE.Mesh(new THREE.SphereGeometry(.12, 20, 16), new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: material.color, emissiveIntensity: 1.6 }));
  beacon.position.y = .78;
  const stem = meshBox(.025, .62, .025, material, 0, .43, 0);
  group.add(base, stem, beacon);

  root.add(group);
  interactables.push(group);
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  anchors.set(entity.id, new THREE.Vector3(origin[0] + x * cos + z * sin, y, origin[1] - x * sin + z * cos));
}

function addCharacterMarker(root, entity, x, y, z, interactables, anchors, material, yaw = 0, origin = [0, 0]) {
  const group = new THREE.Group();
  group.position.set(x, y, z);
  group.userData.entityId = entity.id;
  group.userData.entityKind = entity.kind;
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(.18, .55, 4, 10), material);
  body.position.y = .48;
  const head = new THREE.Mesh(new THREE.SphereGeometry(.17, 16, 12), M.plasterWarm);
  head.position.y = 1.03;
  group.add(body, head);
  root.add(group);
  interactables.push(group);
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  anchors.set(entity.id, new THREE.Vector3(origin[0] + x * cos + z * sin, y, origin[1] - x * sin + z * cos));
}

function addPortal(root, x, y, z, ry, ctx, SY, SP) {
  const g = new THREE.Group();
  g.add(meshBox(.42, 2.1, .45, M.stone, -1.0, 1.05, 0));
  g.add(meshBox(.42, 2.1, .45, M.stone, 1.0, 1.05, 0));
  g.add(meshBox(2.42, .44, .48, M.stone, 0, 2.18, 0));
  g.position.set(x, y, z);
  g.rotation.y = ry;
  root.add(g);
  // pillars block; the lintel is overhead (bottom > feetY + 1.9 handles it)
  addCollider(ctx, rotatedBox(x - 1.0, z, .42, .45, SY, SP[0], SP[1]), .3 + y, .3 + y + 2.1);
  addCollider(ctx, rotatedBox(x + 1.0, z, .42, .45, SY, SP[0], SP[1]), .3 + y, .3 + y + 2.1);
}

function addPlazaDetails(root, x, y, z, ctx, SY, SP) {
  const umbrella = new THREE.Mesh(new THREE.ConeGeometry(1.15, .45, 16, 1, true), M.plasterWarm);
  umbrella.position.set(x - 1.2, y + 2.0, z + .45);
  umbrella.rotation.x = Math.PI;
  root.add(umbrella);
  root.add(meshBox(.06, 1.75, .06, M.ink, x - 1.2, y + 1.05, z + .45));
  root.add(meshBox(1.4, .08, .45, M.stone, x + 1.25, y + .45, z - .75));
  addCollider(ctx, rotatedBox(x + 1.25, z - .75, 1.4, .45, SY, SP[0], SP[1]), .3 + y, .3 + y + .5);
  for (let i = 0; i < 4; i++) addFlowerPot(root, x - 2.0 + i * 1.15, y, z + 1.45 - (i % 2) * .4);
}

function addMirador(root, x, y, z, ctx, SY, SP) {
  const rail = new THREE.Group();
  for (let i = -3; i <= 3; i++) rail.add(meshBox(.035, .88, .035, M.ink, 2.9, .48, i * .65));
  rail.add(meshBox(.05, .05, 4.2, M.ink, 2.9, .92, 0));
  rail.position.set(x, y, z);
  root.add(rail);
  addCollider(ctx, rotatedBox(x + 2.9, z, .1, 4.2, SY, SP[0], SP[1]), .3 + y, .3 + y + .95);
  root.add(meshBox(1.6, .1, .52, M.stone, x + .5, y + .42, z - 1.25));
  addTree(root, x - 1.8, y, z + 1.4, .75, ctx, SY, SP);
}

function addSteps(root, x, z, y0, y1, count, yaw, ctx, SY, SP) {
  const total = y1 - y0;
  const run = 1.45;
  for (let i = 0; i < count; i++) {
    const t = (i + .5) / count;
    const step = meshBox(1.65, .11, run / count + .08, M.cobble, 0, 0, 0);
    step.position.set(x + Math.sin(yaw) * (t - .5) * run, y0 + total * t, z + Math.cos(yaw) * (t - .5) * run);
    step.rotation.y = yaw;
    root.add(step);
    const lx = x + Math.sin(yaw) * (t - .5) * run;
    const lz = z + Math.cos(yaw) * (t - .5) * run;
    addWalkable(ctx, rotatedBox(lx, lz, 1.65, run / count + .15, SY, SP[0], SP[1]), .3 + y0 + total * t + .055);
  }
}

function addTree(root, x, y, z, scale = 1, ctx, SY = 0, SP = [0, 0]) {
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(.10 * scale, .14 * scale, 1.4 * scale, 8), M.terracotta);
  trunk.position.set(x, y + .7 * scale, z);
  const crown = new THREE.Mesh(new THREE.SphereGeometry(.75 * scale, 9, 7), M.greenDark);
  crown.scale.set(1, .9, 1);
  crown.position.set(x, y + 1.7 * scale, z);
  root.add(trunk, crown);
  if (ctx) addCollider(ctx, rotatedBox(x, z, .3 * scale, .3 * scale, SY, SP[0], SP[1]), y, y + 1.4 * scale);
}

function addPalm(root, x, y, z, scale = 1, ctx) {
  const rng = ctx?.rng || (() => .5);
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(.07 * scale, .14 * scale, 2.35 * scale, 7), M.terracotta);
  trunk.position.set(x, y + 1.175 * scale, z);
  trunk.rotation.z = (rng() - .5) * .09;
  root.add(trunk);
  // two-tier drooping crown above walk eye height (walkable .21 + EYE 1.62)
  const crownY = y + 2.42 * scale;
  for (let tier = 0; tier < 2; tier += 1) {
    const leaves = tier === 0 ? 5 : 4;
    const droop = tier === 0 ? -.10 : -.42;
    const len = (tier === 0 ? 1.25 : 1.0) * scale;
    for (let i = 0; i < leaves; i += 1) {
      const material = rng() > .5 ? M.greenDark : M.green;
      const leaf = meshBox(len, .035, .16 * scale, material, x, crownY - tier * .1 * scale, z);
      leaf.rotation.y = (i / leaves) * Math.PI * 2 + tier * .5 + rng() * .2;
      leaf.rotation.z = droop + (rng() - .5) * .08;
      leaf.translateX((tier === 0 ? .55 : .45) * scale);
      root.add(leaf);
    }
  }
  if (ctx) addCollider(ctx, { x0: x - .15, x1: x + .15, z0: z - .15, z1: z + .15 }, y, y + 2.35 * scale);
}

// Umbrella pine — dry-landscape Mediterranean cue (docs/04).
function addPine(root, x, y, z, scale = 1) {
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(.08 * scale, .12 * scale, 1.5 * scale, 7), M.terracotta);
  trunk.position.set(x, y + .75 * scale, z);
  const canopy = new THREE.Mesh(new THREE.SphereGeometry(1.0 * scale, 10, 7), M.pine);
  canopy.scale.set(1.15, .5, 1.15);
  canopy.position.set(x, y + 1.7 * scale, z);
  canopy.castShadow = canopy.receiveShadow = true;
  root.add(trunk, canopy);
}

// Bougainvillea cascade — magenta clusters on facades/walls (micro-DNA).
function addBougainvillea(root, x, y, z, scale, rng) {
  for (let i = 0; i < 5; i += 1) {
    const s = new THREE.Mesh(new THREE.SphereGeometry(.17 * scale, 7, 6), M.bougainvillea[i % 3]);
    s.position.set(x + (rng() - .5) * .55 * scale, y + (rng() - .3) * .7 * scale, z + (rng() - .5) * .18 * scale);
    s.scale.y = .8;
    root.add(s);
  }
}

function addFlowerPot(root, x, y, z) {
  const pot = new THREE.Mesh(new THREE.CylinderGeometry(.16, .12, .25, 12), M.terracotta);
  pot.position.set(x, y + .12, z);
  const flower = new THREE.Mesh(new THREE.SphereGeometry(.23, 8, 6), M.flower);
  flower.scale.y = .7;
  flower.position.set(x, y + .42, z);
  root.add(pot, flower);
}

function meshBox(w, h, d, material, x = 0, y = 0, z = 0) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  mesh.position.set(x, y, z);
  return mesh;
}
